import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import ScaleGauge from './ScaleGauge';
import { printRawBase64 } from '../../utils/qzPrint';
import './DepartmentScreen.css';
import './PouringScreen.css';

/**
 * Pouring Station Screen (No Login Required)
 * Simple scan-based interface for workers with messy hands
 * - Scan CART barcode: single scan marks as poured, moves to labeling
 * - Scan BLEND barcode: opens blending interface with scale
 */
function PouringScreen() {
  // UI state
  const [loading, setLoading] = useState(true);
  const [manualInput, setManualInput] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [dailyTotal, setDailyTotal] = useState(0);
  const [lastScan, setLastScan] = useState(null);

  // Blend state
  const [activeBlend, setActiveBlend] = useState(null);
  const [blendStep, setBlendStep] = useState('idle'); // 'idle', 'verify-oil', 'weighing'
  const [oilVerified, setOilVerified] = useState(false);
  const [oilInventory, setOilInventory] = useState(null);
  const [overridePending, setOverridePending] = useState(false);
  const [pendingOilLink, setPendingOilLink] = useState(null);
  // Big blends are poured as multiple carryable (~30 lb) buckets; each
  // CONFIRM captures one bucket and the totals accumulate toward target.
  const [captures, setCaptures] = useState([]);

  // Scale state
  const [scaleConnected, setScaleConnected] = useState(false);
  const [currentWeight, setCurrentWeight] = useState(0);
  const [isStable, setIsStable] = useState(false);

  const scanInputRef = useRef(null);
  const scanBuffer = useRef('');
  const scanTimeout = useRef(null);
  const handleScanRef = useRef(null);
  const handleCommandRef = useRef(null);
  const lastLabelRef = useRef(null);
  const wsRef = useRef(null);

  // Connect to the scale. Prefer the LOCAL scale bridge on the station PC
  // (ws://localhost:8765, see apps/run/station-scale-bridge/) — the server
  // on Railway has no serial port. Falls back to the legacy server socket
  // for a locally-hosted server, and keeps cycling between the two.
  const connectToScale = useCallback(() => {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const urls = [
      'ws://localhost:8765',
      'ws://127.0.0.1:8765', // Windows can resolve localhost to ::1 while the bridge binds IPv4
      `${protocol}//${window.location.host}/ws/scale`,
    ];
    let idx = 0;

    const open = () => {
      let socket;
      try {
        socket = new WebSocket(urls[idx]);
      } catch (e) {
        idx = (idx + 1) % urls.length;
        setTimeout(open, 2000);
        return;
      }
      wsRef.current = socket;
      let everOpened = false;

      socket.onopen = () => {
        everOpened = true;
        setScaleConnected(true);
      };
      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.weight !== undefined) {
            setCurrentWeight(data.weight);
            setIsStable(data.stable || false);
          }
        } catch (e) {
          console.error('Error parsing scale data:', e);
        }
      };
      socket.onclose = () => {
        setScaleConnected(false);
        if (!everOpened) idx = (idx + 1) % urls.length;
        setTimeout(open, everOpened ? 5000 : 2000);
      };
      socket.onerror = () => {};
    };
    open();
  }, []);

  // Ask the scale (via the local bridge) to zero itself.
  const sendTare = useCallback(() => {
    const socket = wsRef.current;
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ cmd: 'tare' }));
      setSuccessMessage('Tare sent');
    } else {
      setSuccessMessage('Scale not connected');
    }
    setTimeout(() => setSuccessMessage(''), 3000);
  }, []);

  // Fetch daily total
  const fetchDailyTotal = useCallback(async () => {
    try {
      const response = await axios.get('/api/departments/pouring/daily-total');
      setDailyTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching daily total:', error);
    }
  }, []);

  // Initialize
  useEffect(() => {
    setLoading(false);
    connectToScale();
    fetchDailyTotal();

    const refreshInterval = setInterval(fetchDailyTotal, 30000);

    // Focus scan input
    if (scanInputRef.current) scanInputRef.current.focus();

    return () => {
      clearInterval(refreshInterval);
      if (wsRef.current) wsRef.current.close();
    };
  }, [connectToScale, fetchDailyTotal]);

  // Handle cart scan - single scan to mark poured
  const handleCartScan = useCallback(async (barcode) => {
    try {
      const cartResponse = await axios.get(`/api/carts/barcode/${barcode}`);
      const cart = cartResponse.data;

      // Calculate total units
      const totalUnits = cart.cart_items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;

      // Single scan - mark as poured and move to labeling
      await axios.post(`/api/carts/${cart.id}/complete-pouring`, {
        scannedBy: 'Pouring Station'
      });

      setLastScan({
        barcode: cart.barcode,
        cartNumber: cart.cart_number?.replace(/^CART-\d{8}-/, '') || cart.barcode,
        units: totalUnits,
        time: new Date()
      });

      fetchDailyTotal();
      setSuccessMessage(`Poured! ${totalUnits} units`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error processing cart:', error);
      setSuccessMessage(error.response?.data?.error || 'Cart not found');
      setTimeout(() => setSuccessMessage(''), 3000);
    }
  }, [fetchDailyTotal]);

  // Handle blend scan
  const handleBlendScan = useCallback(async (barcode) => {
    try {
      const blendNumber = barcode.trim();
      const response = await axios.get(`/api/blends/barcode/${encodeURIComponent(blendNumber)}`);
      const blendData = response.data;

      setActiveBlend(blendData);
      setBlendStep('verify-oil');
      setOilVerified(false);
      setOilInventory(null);
      setSuccessMessage(`Blend ${blendData.blend_number}`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error loading blend:', error);
      setSuccessMessage(error.response?.data?.error || 'Blend not found');
      setTimeout(() => setSuccessMessage(''), 3000);
    }
  }, []);

  // Handle oil verification scan
  const handleOilScan = useCallback(async (barcode) => {
    if (!activeBlend) return;

    try {
      const response = await axios.post(`/api/blends/${activeBlend.id}/verify-oil`, {
        scannedBarcode: barcode
      });

      if (response.data.verified) {
        setOilVerified(true);
        setOilInventory(response.data.inventory || null);
        setPendingOilLink(null);
        setBlendStep('weighing');
        setSuccessMessage('Oil verified!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else if (response.data.unknownCode) {
        // Never-seen vendor barcode: offer scan-CONFIRM-to-link. Message
        // stays up until CONFIRM, CANCEL, or another scan.
        setPendingOilLink(response.data.scannedCode);
        setSuccessMessage(response.data.error);
      } else {
        setPendingOilLink(null);
        setSuccessMessage(response.data.error || 'Wrong oil');
        setTimeout(() => setSuccessMessage(''), 4000);
      }
    } catch (error) {
      console.error('Error verifying oil:', error);
      setSuccessMessage('Verification failed');
      setTimeout(() => setSuccessMessage(''), 3000);
    }
  }, [activeBlend]);

  // Main scan handler
  const handleScan = useCallback(async (barcode) => {
    const normalizedBarcode = barcode.trim().toUpperCase();
    setManualInput('');

    if (normalizedBarcode.startsWith('CMD-')) {
      if (handleCommandRef.current) handleCommandRef.current(normalizedBarcode);
    } else if (blendStep === 'verify-oil' && activeBlend) {
      await handleOilScan(normalizedBarcode);
    } else if (normalizedBarcode.startsWith('BLEND')) {
      await handleBlendScan(normalizedBarcode);
    } else {
      const cartBarcode = normalizedBarcode.match(/(CART-[\w-]+)/i)?.[1] || normalizedBarcode;
      await handleCartScan(cartBarcode);
    }

    if (scanInputRef.current) scanInputRef.current.focus();
  }, [blendStep, activeBlend, handleOilScan, handleBlendScan, handleCartScan]);

  handleScanRef.current = handleScan;

  // Complete blending. Captures the current weight, then completes. A weight
  // outside tolerance does NOT pop a dialog (hands are oily; dialogs also
  // block the kiosk) — it arms an on-screen override that a second CONFIRM
  // scan accepts and a CANCEL scan clears.
  const completeBlending = useCallback(async (force = false, totalLbs = null) => {
    if (!activeBlend) return;

    const targetWeight = parseFloat(activeBlend.total_oil_lbs) || 0;
    const capturedTotal = captures.reduce((a, b) => a + b, 0);
    const actual = totalLbs != null ? totalLbs : (capturedTotal > 0 ? capturedTotal : currentWeight);

    if (!force && Math.abs(actual - targetWeight) > 0.5) {
      setOverridePending(true);
      setSuccessMessage(
        `Total ${actual.toFixed(2)} lbs vs target ${targetWeight.toFixed(2)} lbs — scan CONFIRM again to accept, CANCEL to abort`
      );
      return;
    }
    setOverridePending(false);

    try {
      await axios.post(`/api/blends/${activeBlend.id}/complete-blending`, {
        actualOilLbs: actual,
        blendedBy: 'Pouring Station'
      });

      setSuccessMessage(`Blend complete! ${actual.toFixed(2)} lbs${captures.length > 1 ? ` in ${captures.length} buckets` : ''}`);
      setTimeout(() => {
        setSuccessMessage('');
        setActiveBlend(null);
        setBlendStep('idle');
        setOilVerified(false);
        setOilInventory(null);
        setCaptures([]);
      }, 2500);
    } catch (error) {
      setSuccessMessage(error.response?.data?.error || 'Failed');
      setTimeout(() => setSuccessMessage(''), 3000);
    }
  }, [activeBlend, currentWeight, captures]);

  // Print the sticky bucket ticket that travels to the melter. Server prints
  // to a networked Zebra when configured; otherwise the returned ZPL goes to
  // the station's local bridge for an attached label printer.
  const printBucketLabel = useCallback(async (bucketNumber, weightLbs, blendId = null) => {
    const id = blendId || (activeBlend && activeBlend.id);
    if (!id) return;
    lastLabelRef.current = { blendId: id, bucketNumber, weightLbs };
    try {
      const response = await axios.post(`/api/blends/${id}/bucket-label`, {
        bucketNumber,
        weightLbs,
      });
      if (!response.data.printed && (response.data.zpl || response.data.escpos_b64)) {
        // Same path as pick tickets: QZ Tray on the station PC. Falls back
        // to the scale bridge's shared-printer print if QZ isn't running.
        let qzPrinted = false;
        if (response.data.escpos_b64) {
          try {
            await printRawBase64(response.data.escpos_b64);
            qzPrinted = true;
          } catch (qzError) {
            console.error('QZ label print unavailable, trying bridge:', qzError.message);
          }
        }
        if (!qzPrinted) {
          const socket = wsRef.current;
          if (socket && socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({
              cmd: 'print',
              zpl: response.data.zpl,
              escpos_b64: response.data.escpos_b64,
            }));
          }
        }
      }
    } catch (e) {
      console.error('Bucket label print failed (non-fatal):', e);
    }
  }, [activeBlend]);

  // Capture the bucket currently on the scale, then complete automatically
  // when the running total lands within tolerance of the blend target.
  const captureBucket = useCallback(() => {
    if (!activeBlend) return;
    const targetWeight = parseFloat(activeBlend.total_oil_lbs) || 0;
    const newCaptures = [...captures, currentWeight];
    const newTotal = newCaptures.reduce((a, b) => a + b, 0);

    axios.post(`/api/blends/${activeBlend.id}/capture-weight`, {
      weight: currentWeight,
      isStable,
      timestamp: new Date().toISOString()
    }).catch((e) => console.error('Weight capture failed (non-fatal):', e));

    setCaptures(newCaptures);
    printBucketLabel(newCaptures.length, currentWeight);

    if (Math.abs(newTotal - targetWeight) <= 0.5) {
      completeBlending(true, newTotal);
    } else if (newTotal > targetWeight + 0.5) {
      setOverridePending(true);
      setSuccessMessage(
        `Total ${newTotal.toFixed(2)} lbs is OVER target ${targetWeight.toFixed(2)} — scan CONFIRM to accept, CANCEL to abort`
      );
    } else {
      setSuccessMessage(
        `Bucket ${newCaptures.length}: ${currentWeight.toFixed(2)} lbs — total ${newTotal.toFixed(2)} of ${targetWeight.toFixed(2)}. Swap buckets, TARE, keep pouring.`
      );
      setTimeout(() => setSuccessMessage(''), 6000);
    }
  }, [activeBlend, captures, currentWeight, isStable, completeBlending, printBucketLabel]);

  // Cancel blend
  const cancelBlend = useCallback(() => {
    setActiveBlend(null);
    setBlendStep('idle');
    setOilVerified(false);
    setOilInventory(null);
    setOverridePending(false);
    setCaptures([]);
    setSuccessMessage('');
    if (scanInputRef.current) scanInputRef.current.focus();
  }, []);

  // Printed command barcodes (CMD-CONFIRM / CMD-CANCEL / CMD-TARE on the
  // laminated station card) — the whole flow runs from the scanner.
  const handleCommand = useCallback((command) => {
    switch (command) {
      case 'CMD-CANCEL':
        if (pendingOilLink) {
          // Abort just the link offer; keep the blend so the right drum can be scanned.
          setPendingOilLink(null);
          setSuccessMessage('Link cancelled — scan the correct oil');
          setTimeout(() => setSuccessMessage(''), 3000);
        } else {
          cancelBlend();
          setSuccessMessage('Cancelled');
          setTimeout(() => setSuccessMessage(''), 2000);
        }
        break;
      case 'CMD-TARE':
        sendTare();
        break;
      case 'CMD-REPRINT':
        if (lastLabelRef.current) {
          const last = lastLabelRef.current;
          printBucketLabel(last.bucketNumber, last.weightLbs, last.blendId);
          setSuccessMessage(`Reprinting bucket ${last.bucketNumber} ticket`);
        } else {
          setSuccessMessage('No ticket to reprint yet');
        }
        setTimeout(() => setSuccessMessage(''), 2500);
        break;
      case 'CMD-CONFIRM':
        if (blendStep === 'verify-oil' && activeBlend && pendingOilLink) {
          axios.post(`/api/blends/${activeBlend.id}/link-oil-barcode`, {
            scannedBarcode: pendingOilLink,
            linkedBy: 'Pouring Station'
          }).then((response) => {
            setPendingOilLink(null);
            setOilVerified(true);
            setBlendStep('weighing');
            setSuccessMessage(response.data.message || 'Barcode linked — oil verified');
            setTimeout(() => setSuccessMessage(''), 3000);
          }).catch(() => {
            setSuccessMessage('Link failed — try scanning the oil again');
            setTimeout(() => setSuccessMessage(''), 3000);
          });
        } else if (blendStep === 'weighing' && activeBlend) {
          if (overridePending) {
            completeBlending(true);
          } else if (!isStable && scaleConnected && currentWeight >= 1) {
            setSuccessMessage('Wait for a stable reading, then scan CONFIRM');
            setTimeout(() => setSuccessMessage(''), 3000);
          } else if (currentWeight >= 1) {
            captureBucket();
          } else if (captures.length > 0) {
            // Empty scale + captured buckets = done pouring.
            completeBlending(false);
          } else {
            // No scale data at all (e.g. bridge not installed yet).
            completeBlending(false);
          }
        } else {
          setSuccessMessage('Nothing to confirm');
          setTimeout(() => setSuccessMessage(''), 2000);
        }
        break;
      default:
        setSuccessMessage(`Unknown command: ${command}`);
        setTimeout(() => setSuccessMessage(''), 2000);
    }
  }, [blendStep, activeBlend, overridePending, pendingOilLink, isStable, scaleConnected, currentWeight, captures, cancelBlend, sendTare, completeBlending, captureBucket, printBucketLabel]);

  handleCommandRef.current = handleCommand;

  // Global keydown handler for barcode scanner
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (e.key === 'F5' || (e.ctrlKey && e.key === 'r')) {
        e.preventDefault();
        return;
      }

      // Focus scan input
      if (scanInputRef.current && document.activeElement !== scanInputRef.current) {
        scanInputRef.current.focus();
      }

      // Buffer scanner input
      if (e.key.length === 1) {
        scanBuffer.current += e.key;
        if (scanTimeout.current) clearTimeout(scanTimeout.current);

        scanTimeout.current = setTimeout(() => {
          const scanned = scanBuffer.current.trim();
          if (scanned && scanned.length > 3 && handleScanRef.current) {
            handleScanRef.current(scanned);
          }
          scanBuffer.current = '';
        }, 100);
      }

      if (e.key === 'Enter') {
        e.preventDefault();
        if (scanBuffer.current.trim() && handleScanRef.current) {
          handleScanRef.current(scanBuffer.current.trim());
          scanBuffer.current = '';
          if (scanTimeout.current) clearTimeout(scanTimeout.current);
        }
      }
    };

    window.addEventListener('keydown', handleGlobalKeyDown, true);

    return () => {
      window.removeEventListener('keydown', handleGlobalKeyDown, true);
      if (scanTimeout.current) clearTimeout(scanTimeout.current);
    };
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (manualInput.trim()) {
        handleScan(manualInput.trim());
      }
    }
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  // Calculate blend oil info
  const targetOilLbs = activeBlend?.total_oil_lbs ? parseFloat(activeBlend.total_oil_lbs) : 0;
  const oilOnHand = oilInventory?.current_quantity_lbs ? parseFloat(oilInventory.current_quantity_lbs) : 0;
  const oilAfterBlend = oilOnHand - targetOilLbs;

  if (loading) {
    return (
      <div className="department-screen loading">
        <div className="loader">Loading...</div>
      </div>
    );
  }

  return (
    <div className="pouring-station">
      {/* Header */}
      <div className="pouring-header">
        <h1 className="pouring-title">POURING</h1>
        <div className="pouring-header-right">
          <div className={`scale-indicator ${scaleConnected ? 'connected' : ''}`}>
            Scale: {scaleConnected ? 'ON' : 'OFF'}
          </div>
          <input
            ref={scanInputRef}
            type="text"
            value={manualInput}
            onChange={(e) => setManualInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={blendStep === 'verify-oil' ? 'Scan oil...' : 'Scan...'}
            className="pouring-scan-input"
            autoFocus
          />
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className={`pouring-message ${successMessage.toLowerCase().includes('fail') || successMessage.toLowerCase().includes('not') || successMessage.toLowerCase().includes('wrong') ? 'error' : 'success'}`}>
          {successMessage}
        </div>
      )}

      {/* Main Content */}
      {!activeBlend ? (
        <div className="pouring-main">
          {/* Daily Total */}
          <div className="daily-total-section">
            <div className="daily-total-number">{dailyTotal.toLocaleString()}</div>
            <div className="daily-total-label">units today</div>
          </div>

          {/* Last Scan Info */}
          {lastScan && (
            <div className="last-scan-info">
              Last: Cart #{lastScan.cartNumber} ({lastScan.units} units) at {formatTime(lastScan.time)}
            </div>
          )}
        </div>
      ) : blendStep === 'weighing' && oilVerified ? (
        /* WEIGHING — the whole screen belongs to the scale. Scanner-only. */
        <div className="blend-weighing-full">
          <div className="blend-compact-strip">
            <span className="strip-blend">{activeBlend.blend_number}</span>
            <span className="strip-oil">
              {(activeBlend.fragrance_oil_name || activeBlend.fragrance_name || '').split(' - ')[0] || ''}
            </span>
            <span className="strip-verified">OIL VERIFIED</span>
          </div>
          {captures.length > 0 && (
            <div className="blend-total-row">
              <span className="blend-total-big">
                {captures.reduce((a, b) => a + b, 0).toFixed(2)} / {targetOilLbs.toFixed(2)} lbs
              </span>
              <span className="blend-total-buckets">
                {captures.map((w, i) => `#${i + 1}: ${w.toFixed(1)}`).join('  ·  ')}
              </span>
            </div>
          )}
          <ScaleGauge
            currentWeight={currentWeight}
            targetWeight={Math.max(0, targetOilLbs - captures.reduce((a, b) => a + b, 0))}
            isStable={isStable}
            tolerance={0.5}
            unit="lbs"
          />
          <div className="blend-scan-hints">
            {overridePending
              ? 'Off target — scan CONFIRM again to accept this total, or CANCEL to abort'
              : captures.length > 0
                ? 'Gauge shows what is LEFT · pour, scan CONFIRM per bucket · CONFIRM on empty scale finishes'
                : 'Pour, then scan CONFIRM · TARE zeroes an empty bucket · CANCEL aborts'}
          </div>
        </div>
      ) : (
        /* VERIFY OIL step */
        <div className="blend-interface">
          <div className="blend-header-card">
            <div className="blend-number-display">{activeBlend.blend_number}</div>
            <div className="blend-fragrance">
              {((activeBlend.fragrance_name || activeBlend.fragrance_oil_name || 'Unknown').split(' - ')[0] || 'Unknown').trim()}
            </div>
          </div>

          <div className="blend-section">
            <div className="blend-section-header">
              <span>FRAGRANCE OIL</span>
              <span className={`oil-badge ${oilVerified ? 'verified' : 'pending'}`}>
                {oilVerified ? 'VERIFIED' : 'SCAN OIL'}
              </span>
            </div>
            <div className="oil-verify-prompt">
              <p>{pendingOilLink
                ? 'Check the drum label, then scan CONFIRM to link it — or CANCEL to scan a different drum'
                : 'Scan any barcode on the oil drum'}</p>
              <p className="expected-label">
                Expected: {(activeBlend.fragrance_oil_name || activeBlend.fragrance_name || '').split(' - ')[0] || activeBlend.fragrance_oil_id || 'N/A'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PouringScreen;
